import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Dialog, DialogContent, DialogTitle, IconButton, Skeleton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AnalyticsService } from '../../../../core/services';
import { DummyCampaignImage } from '../../../../config/config';

const analyticsService = new AnalyticsService();

// user_role_id values allowed to enter views by hand: super_admin (1),
// admin (2), bd_admin (3). Mirrors Analytics.MANUAL_VIEWS_ROLE_IDS on the API.
export const MANUAL_VIEWS_ROLE_IDS = [1, 2, 3];

export const useCanEditViews = () => {
  const user = useSelector((state: any) => state?.UserReducer?.UserData);
  return {
    user,
    canEditViews: MANUAL_VIEWS_ROLE_IDS.includes(Number(user?.user_role_id)),
  };
};

type Props = {
  open: boolean;
  onClose: () => void;
  campaignId: number | string;
  /** The posts already on screen — the grid reuses their identity rather than
   *  re-deriving it, since the Bright Data posts table is sparse for
   *  campaign-analytics posts (empty username/shortcode). */
  posts: any[];
  /** selectCampaign.campaign_platform — decides how the post link is built. */
  platform?: string;
  onSaved?: () => void;
};

/**
 * Posts (rows) x dates (columns) editor for views Bright Data didn't return.
 *
 * Only dates that already have a record are offered as columns, so every save
 * is an UPDATE — nothing is ever inserted. A cell that already holds a real
 * number is read-only: the API refuses to overwrite fetched data, so it is
 * shown as plain text rather than an input that would error on submit.
 */
const ManualViewsGrid = (props: Props) => {
  const { user } = useCanEditViews();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dates, setDates] = useState<string[]>([]);
  const [values, setValues] = useState<any>({});
  // Only what the admin actually typed, keyed "url_id|date".
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [error, setError] = useState('');

  useEffect(() => {
    if (!props.open) return;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError('');
        setEdits({});
        const response = await analyticsService.analyticsGetManualViewsGrid({
          campaign_id: props.campaignId,
          user_id: user?.user_id,
        });
        if (cancelled) return;
        if (response?.status) {
          setDates(response?.data?.dates ?? []);
          setValues(response?.data?.values ?? {});
        } else {
          setError(response?.info || 'Could not load the views grid');
        }
      } catch (err) {
        if (!cancelled) setError('Could not load the views grid');
        console.error(`ManualViewsGrid load ERROR ::: ${err}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [props.open, props.campaignId, user?.user_id]);

  // Same link shape the posts table uses. Falls back to the row's own fields so
  // the grid still works if `platform` isn't passed; if neither identifier is
  // present we render plain text rather than a dead link.
  const postUrl = (post: any) => {
    // Brand Pulse rows already carry the resolved link; campaign analytics rows
    // carry only the identifier, so build it from that.
    if (post?.postUrl || post?.post_url) return post.postUrl || post.post_url;
    const isYoutube = `${props.platform ?? ''}`.toLowerCase() === 'youtube' || (!post?.shortcode && post?.video_id);
    if (isYoutube) return post?.video_id ? `https://www.youtube.com/watch?v=${post.video_id}` : '';
    return post?.shortcode ? `https://www.instagram.com/p/${post.shortcode}` : '';
  };

  const key = (urlId: any, date: string) => `${urlId}|${date}`;

  const pendingCount = useMemo(
    () => Object.values(edits).filter((v) => v !== '').length,
    [edits]
  );

  const handleSave = async () => {
    const entries = Object.entries(edits)
      .filter(([, v]) => v !== '')
      .map(([k, v]) => {
        const [url_id, entry_date] = k.split('|');
        return { url_id: Number(url_id), entry_date, views: Number(v) };
      })
      .filter((e) => Number.isInteger(e.views) && e.views >= 0);

    if (entries.length === 0) {
      setError('Enter at least one value');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const response = await analyticsService.analyticsSaveManualViewsBulk({
        campaign_id: props.campaignId,
        user_id: user?.user_id,
        entries,
      });

      if (response?.status) {
        // Fold the accepted values into the grid so the cells become read-only
        // without a refetch; anything the API skipped keeps its input.
        const skipped = new Set(
          (response?.data?.skipped ?? []).map((s: any) => key(s.url_id, s.entry_date))
        );
        const nextValues = { ...values };
        const nextEdits = { ...edits };
        entries.forEach((e) => {
          const k = key(e.url_id, e.entry_date);
          if (skipped.has(k)) return;
          nextValues[e.url_id] = { ...(nextValues[e.url_id] || {}), [e.entry_date]: e.views };
          delete nextEdits[k];
        });
        setValues(nextValues);
        setEdits(nextEdits);
        if (skipped.size > 0) {
          setError(`${response?.data?.skipped?.[0]?.reason ?? 'Some rows were skipped'} (${skipped.size} skipped)`);
        } else {
          props.onSaved?.();
          props.onClose();
        }
      } else {
        setError(response?.info || 'Views not updated');
      }
    } catch (err) {
      console.error(`ManualViewsGrid save ERROR ::: ${err}`);
      setError('Views not updated');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth="xl" fullWidth>
      <DialogTitle className="d-flex align-items-center justify-content-between">
        <div>
          <div>Update Views</div>
          <div style={{ fontSize: 13, color: '#64748b', fontWeight: 400 }}>
            Fill in views Bright Data didn&apos;t return. Only dates that already have a record are shown; cells with a fetched value can&apos;t be changed.
          </div>
        </div>
        <IconButton onClick={props.onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Skeleton variant="rectangular" height={320} />
        ) : dates.length === 0 ? (
          <p className="text-slate-500 mb-0">No records found for this campaign.</p>
        ) : (
          <div style={{ overflowX: 'auto', maxHeight: '60vh' }}>
            <table className="table table-bordered mb-0" style={{ minWidth: 700 }}>
              <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
                <tr>
                  <th style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 3, minWidth: 240 }}>Post</th>
                  {dates.map((d) => (
                    <th key={d} className="text-center" style={{ minWidth: 130, whiteSpace: 'nowrap' }}>{d}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {props.posts.map((post: any) => {
                  const urlId = post?.url_id ?? post?.id;
                  return (
                    <tr key={urlId}>
                      <td style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>
                        {(() => {
                          const href = postUrl(post);
                          const body = (
                            <div className="d-flex align-items-center gap-2">
                              <img
                                src={post?.gcp_display_url || post?.image || post?.thumbnails || DummyCampaignImage.default_campaign_image}
                                alt=""
                                style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                              />
                              <div style={{ minWidth: 0 }}>
                                <div className="text-truncate" style={{ maxWidth: 180 }}>
                                  {post?.full_name || post?.channel_title || post?.title || post?.username}
                                </div>
                                <div className="text-truncate" style={{ maxWidth: 180, fontSize: 12, color: '#64748b' }}>
                                  {post?.username ? `@${post.username}` : ''}
                                </div>
                              </div>
                            </div>
                          );
                          if (!href) return body;
                          return (
                            <Tooltip title="Open post in a new tab" placement="top">
                              <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-decoration-none text-body"
                              >
                                {body}
                              </a>
                            </Tooltip>
                          );
                        })()}
                      </td>

                      {dates.map((d) => {
                        const existing = values?.[urlId]?.[d];
                        const hasRecord = existing !== undefined && existing !== null;
                        const isLocked = hasRecord && Number(existing) > 0;

                        if (!hasRecord) {
                          // No row for this post on this date — nothing to update.
                          return <td key={d} className="text-center text-slate-400">—</td>;
                        }
                        if (isLocked) {
                          return (
                            <td key={d} className="text-center">
                              <Tooltip title="Fetched from Bright Data" placement="top">
                                <span>{Number(existing).toLocaleString()}</span>
                              </Tooltip>
                            </td>
                          );
                        }
                        return (
                          <td key={d} className="text-center">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              className="form-control form-control-sm text-end"
                              placeholder="0"
                              value={edits[key(urlId, d)] ?? ''}
                              onChange={(e) =>
                                setEdits((prev) => ({ ...prev, [key(urlId, d)]: e.target.value }))
                              }
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {error && <p className="error mt-2 mb-0">{error}</p>}
      </DialogContent>

      <div className="d-flex align-items-center justify-content-end gap-2 p-3">
        <span className="me-auto text-slate-500" style={{ fontSize: 13 }}>
          {pendingCount > 0 ? `${pendingCount} value${pendingCount > 1 ? 's' : ''} to save` : ''}
        </span>
        <button className="btn btn-outline-secondary btn-sm" onClick={props.onClose} disabled={saving}>
          Cancel
        </button>
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || loading || pendingCount === 0}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </Dialog>
  );
};

export default ManualViewsGrid;
